// ═══════════════════════════════════════════════════════════════════════════════
// PWA shell height — Android pinch-zoom uses visualViewport; iOS uses latched baseline
// so keyboard dismiss does not leave innerHeight stuck short.
// ═══════════════════════════════════════════════════════════════════════════════

import { isIosMobilePwa } from './pwaRuntime';

export const VIEWPORT_ZOOM_THRESHOLD = 1.01;
export const KEYBOARD_OPEN_HEIGHT_DELTA_PX = 120;
export const KEYBOARD_CLOSE_RESYNC_DELAYS_MS = [0, 100, 300, 500, 800] as const;
/** Route change resync includes longer tail for iOS layout viewport catch-up. */
export const ROUTE_CHANGE_RESYNC_DELAYS_MS = [0, 100, 300, 500, 800, 1200] as const;

const resolveVisualViewportExtentPx = (): number => {
  const visualViewport = window.visualViewport;
  if (!visualViewport) {
    return window.innerHeight;
  }
  return visualViewport.offsetTop + visualViewport.height;
};

const resolveLayoutHeightCandidatesPx = (): { layoutHeight: number; visualExtent: number } => ({
  layoutHeight: Math.max(0, Math.round(window.innerHeight)),
  visualExtent: Math.max(0, Math.round(resolveVisualViewportExtentPx())),
});

let iosShellBaselineHeightPx = 0;

if (typeof window !== 'undefined') {
  const { layoutHeight, visualExtent } = resolveLayoutHeightCandidatesPx();
  iosShellBaselineHeightPx = Math.max(layoutHeight, visualExtent);
}

/** Grow baseline from current metrics; never shrink (safe on mount / route enter). */
export const seedIosShellBaselineHeight = (): void => {
  if (typeof window === 'undefined' || !isIosMobilePwa()) {
    return;
  }
  refreshIosShellBaselineHeight();
};

/** Full baseline reset after device rotation (dimensions may shrink vs portrait latch). */
export const resetIosShellBaselineHeightForOrientation = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const { layoutHeight, visualExtent } = resolveLayoutHeightCandidatesPx();
  iosShellBaselineHeightPx = Math.max(layoutHeight, visualExtent);
};

export const isVirtualKeyboardOpen = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const visualViewport = window.visualViewport;
  if (!visualViewport) {
    return false;
  }

  const scale = visualViewport.scale ?? 1;
  if (scale > VIEWPORT_ZOOM_THRESHOLD) {
    return false;
  }

  const heightDelta = window.innerHeight - visualViewport.height;
  return heightDelta >= KEYBOARD_OPEN_HEIGHT_DELTA_PX;
};

/** Grow baseline only while keyboard is closed; never shrink until orientation reset. */
export const refreshIosShellBaselineHeight = (): void => {
  if (typeof window === 'undefined' || !isIosMobilePwa()) {
    return;
  }
  if (isVirtualKeyboardOpen()) {
    return;
  }

  const { layoutHeight, visualExtent } = resolveLayoutHeightCandidatesPx();
  iosShellBaselineHeightPx = Math.max(
    iosShellBaselineHeightPx,
    layoutHeight,
    visualExtent,
  );
};

export const resolvePwaShellHeightPx = (): number => {
  const visualViewport = window.visualViewport;
  const layoutHeight = window.innerHeight;

  if (!visualViewport) {
    return Math.max(0, Math.round(layoutHeight));
  }

  const scale = visualViewport.scale ?? 1;
  if (scale > VIEWPORT_ZOOM_THRESHOLD) {
    return Math.max(0, Math.round(visualViewport.height));
  }

  if (isIosMobilePwa()) {
    refreshIosShellBaselineHeight();
    return Math.max(0, iosShellBaselineHeightPx);
  }

  return Math.max(0, Math.round(visualViewport.height));
};

export const getVisualViewportBottomPx = (): number => resolveVisualViewportExtentPx();
